import * as React from "react";
import { Plus, Pencil, TriangleAlert, Video, TrendingUp } from "lucide-react";

import { Skeleton, Spinner } from "@/components/ui";
import { PlayerQRCode } from "@/components/PlayerQRCode";
import { useShareFormLink } from "../context/ShareFormLinkContext";

export function SimpleShareView() {
  const {
    showLoader,
    selectedRequest,
    isCustomLink,
    isAiRunning,
    reviewLinkUrl,
    qrLogoSrc,
    handleResetToDefault,
    handleEnterEditMode,
    setShowCustomWorkflow,
    handleOpenVideoRequestWorkflow,
  } = useShareFormLink();

  if (showLoader) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-12 gap-8 w-full animate-pulse select-none p-4">
        {/* Left Panel: Basic Share Stuff Skeleton */}
        <div className="col-span-12 md:col-span-6 flex flex-col items-center gap-4 min-w-0">
          {/* QR Code Canvas Box Skeleton */}
          <div className="bg-slate-50 p-2 rounded-2xl border border-slate-100 flex items-center justify-center shrink-0">
            <Skeleton className="size-[200px] rounded-xl" />
          </div>

          {/* Link Input & Open Link Row Skeleton */}
          <div className="w-full flex gap-2">
            <Skeleton className="h-10 flex-1 rounded-xl" />
            <Skeleton className="h-10 w-10 rounded-xl" />
          </div>

          {/* Copy Button Skeleton */}
          <Skeleton className="h-11 w-full rounded-xl" />

          {/* Download & Share Actions Row Skeleton */}
          <div className="grid grid-cols-2 gap-3 w-full">
            <Skeleton className="h-11 rounded-xl" />
            <Skeleton className="h-11 rounded-xl" />
          </div>
        </div>

        {/* Right Panel: Outreach Booster Options Skeleton */}
        <div className="col-span-12 md:col-span-6 flex flex-col gap-5 bg-slate-50 border border-slate-200/60 rounded-2xl p-6">
          {/* Header Title Skeleton */}
          <div className="flex flex-col gap-1.5 pb-2">
            <Skeleton className="h-7 w-48 rounded-lg" />
          </div>

          {/* Action Options Skeletons */}
          <div className="flex flex-col gap-4 w-full">
            {/* Customize / Personalize Card Skeleton */}
            <div className="p-4 border border-slate-100 rounded-xl bg-white flex flex-col gap-2.5">
              <div className="flex gap-3.5 items-center">
                <Skeleton className="size-10 rounded-xl shrink-0" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <Skeleton className="h-4.5 w-32 rounded-md" />
                  <Skeleton className="h-3.5 w-24 rounded-md" />
                </div>
              </div>
              <Skeleton className="h-4 w-full rounded-md mt-1.5" />
            </div>

            {/* Record Personal Video Card Skeleton */}
            <div className="p-4 border border-slate-100 rounded-xl bg-white flex flex-col gap-2.5">
              <div className="flex gap-3.5 items-center">
                <Skeleton className="size-10 rounded-xl shrink-0" />
                <div className="flex flex-col gap-1.5 flex-1">
                  <Skeleton className="h-4.5 w-40 rounded-md" />
                  <Skeleton className="h-3.5 w-28 rounded-md" />
                </div>
              </div>
              <Skeleton className="h-4 w-full rounded-md mt-1.5" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!selectedRequest) {
    return (
      <div className="flex flex-col items-center gap-4 w-full text-center py-6 px-2 select-none animate-in fade-in duration-200">
        <TriangleAlert className="size-12 text-amber-500 shrink-0" />
        <div>
          <h3 className="text-base font-bold text-slate-800 mb-1">No Review Form Configured</h3>
          <p className="text-sm text-slate-500 leading-normal font-sans">
            A default review form has not been configured yet. Please choose another form from the menu below or contact
            support.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-8 w-full animate-in fade-in duration-200 p-4">
      {/* Left Panel: Basic Share Stuff */}
      <div className="col-span-12 md:col-span-6 flex flex-col items-center gap-4 min-w-0">
        {/* Custom Fan / Form Indicator Banner */}
        {isCustomLink && (
          <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 flex items-center justify-between w-full text-xs text-amber-800 leading-normal select-none font-sans">
            <span className="font-semibold text-left">
              {selectedRequest.fan?.displayName || selectedRequest.internalIdentifier
                ? `Custom link for: ${selectedRequest.fan?.displayName || selectedRequest.internalIdentifier}`
                : "AI Personalized Link"}
            </span>
            <button
              onClick={handleResetToDefault}
              className="text-amber-900 font-bold hover:underline border-none bg-transparent cursor-pointer ml-3 shrink-0 outline-none"
            >
              Use Default
            </button>
          </div>
        )}

        {/* QR Code & Sharing Options */}
        <PlayerQRCode
          id="qr-code-svg-review"
          value={reviewLinkUrl}
          qrLogoSrc={qrLogoSrc}
          title="Leave a Review"
          text="I would appreciate your recent feedback. Please take a moment to leave a review!"
          downloadFileName="review-qr-code.png"
        />
      </div>

      {/* Right Panel: Outreach Booster Options */}
      <div className="col-span-12 md:col-span-6 flex flex-col gap-4 text-left bg-slate-50 border border-slate-200/60 rounded-2xl p-6">
        {/* Nice Header to explain boosting */}
        <div className="flex flex-col gap-1.5">
          <h4 className="font-bold text-slate-800 text-lg flex items-center gap-2">
            <TrendingUp className="size-5.5 text-primary" />
            Boost Your Review Rates
          </h4>
        </div>

        {/* AI Personalization Processing Indicator */}
        {isCustomLink && isAiRunning && (
          <div className="bg-primary/10 border border-indigo-100/50 rounded-xl p-3.5 flex gap-2.5 items-start text-xs text-primary leading-normal w-full select-none font-sans">
            <div className="shrink-0 mt-0.5">
              <Spinner className="size-4.5 text-primary animate-spin shrink-0" />
            </div>
            <div className="flex flex-col gap-1 text-left min-w-0 flex-1">
              <span className="text-sm font-bold text-indigo-950 flex items-center gap-1">
                Personalizing with{" "}
                <img
                  alt={"SocialCoach AI"}
                  src="https://cdn.socialcoach.io/socoai_mono_positive.svg"
                  className="h-[12px]"
                />
              </span>
              <p className="text-xs text-primary font-medium leading-normal">
                <strong>You can share this link now</strong>—the form will auto-update as soon as it's ready!
              </p>
            </div>
          </div>
        )}

        {/* Action Options */}
        <div className="flex flex-col gap-4">
          {isCustomLink ? (
            <>
              {/* Option 2: Record Personal Video (Shown on top when personalized) */}
              <div
                onClick={handleOpenVideoRequestWorkflow}
                className="flex flex-col gap-3 p-4 bg-linear-to-br from-indigo-50/20 via-white to-white hover:bg-slate-50 border-2 border-primary/30 rounded-xl cursor-pointer select-none transition-all outline-none shadow-xs font-sans hover:border-primary/50 group"
              >
                <div className="flex items-start gap-3.5 min-w-0">
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
                    <Video className="size-5" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-bold text-slate-900 group-hover:text-primary leading-tight">
                      Record Personal Video
                    </span>

                    {/* Badges directly below title */}
                    <div className="flex flex-wrap gap-1.5 mt-1 mb-1">
                      <span className="bg-primary/10 text-primary border border-primary/20 text-xs px-2 py-0.5 rounded-full font-medium capitalize">
                        highly recommended
                      </span>
                    </div>

                    <span className="text-sm text-slate-500 font-medium leading-relaxed flex flex-col gap-1">
                      <span>Record a quick, personal video thanking them and asking for their support.</span>
                      <span className="text-slate-400 text-xs italic mt-0.5">
                        Takes about 1–2 minutes. Just read the teleprompter!
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Option 1: Edit Custom Settings (Shown below when personalized) */}
              <div
                onClick={handleEnterEditMode}
                className="flex flex-col gap-3 p-4 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl cursor-pointer select-none transition-all outline-none shadow-xs font-sans hover:border-slate-300 group"
              >
                <div className="flex items-start gap-3.5 min-w-0">
                  <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 group-hover:bg-slate-100 shrink-0">
                    <Pencil className="size-5" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-bold text-slate-800 leading-tight group-hover:text-primary transition-colors">
                      Edit Custom Settings
                    </span>

                    <span className="text-sm text-slate-500 mt-1 font-medium leading-relaxed">
                      Modify the client's name, chosen feedback form, or personalization details.
                    </span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Option 1: Customize & Personalize Link (Shown on top when unpersonalized) */}
              <div
                onClick={() => setShowCustomWorkflow(true)}
                className="flex flex-col gap-3 p-4 bg-white hover:bg-slate-50 border border-slate-200 rounded-xl cursor-pointer select-none transition-all outline-none shadow-xs font-sans hover:border-slate-300 group"
              >
                <div className="flex items-start gap-3.5 min-w-0">
                  <div className="p-2.5 rounded-xl bg-slate-50 border border-slate-200 text-slate-600 group-hover:bg-slate-100 shrink-0">
                    <Plus className="size-5" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-bold text-slate-800 leading-tight group-hover:text-primary transition-colors">
                      Customize & Personalize Link
                    </span>

                    <span className="text-sm text-slate-500 mt-1 font-medium leading-relaxed">
                      Select a client and choose a custom form to generate a tailor-made review link.
                    </span>
                  </div>
                </div>
              </div>

              {/* Option 2: Record Personal Video (Shown below when unpersonalized) */}
              <div
                onClick={handleOpenVideoRequestWorkflow}
                className="flex flex-col gap-3 p-4 bg-linear-to-br from-indigo-50/20 via-white to-white hover:bg-slate-50 border-2 border-primary/30 rounded-xl cursor-pointer select-none transition-all outline-none shadow-xs font-sans hover:border-primary/50 group"
              >
                <div className="flex items-start gap-3.5 min-w-0">
                  <div className="p-2.5 rounded-xl bg-primary/10 text-primary shrink-0">
                    <Video className="size-5" />
                  </div>
                  <div className="flex flex-col min-w-0">
                    <span className="text-sm font-bold text-slate-900 group-hover:text-primary leading-tight">
                      Record Personal Video
                    </span>

                    {/* Badges directly below title */}
                    <div className="flex flex-wrap gap-1.5 mt-1 mb-1">
                      <span className="bg-primary/10 text-primary border border-primary/20 text-xs px-2 py-0.5 rounded-full font-medium capitalize">
                        highly recommended
                      </span>
                    </div>

                    <span className="text-sm text-slate-500 font-medium leading-relaxed flex flex-col gap-1">
                      <span>Record a quick, personal video thanking them and asking for their support.</span>
                      <span className="text-slate-400 text-xs italic mt-0.5">
                        Takes about 1–2 minutes. Just read the teleprompter!
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
